import React, { useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { ErrorState, LoadingState, PageHeader } from '@/components/primitives';
import useResource from '@/pages/dashboard/useResource';
import { apiGet } from '@/pages/dashboard/api';
import { ago } from '@/pages/dashboard/format';
import { TypeBadge } from './actorUi';
import '@/styles/actors.css';

/*
 * Threat actor directory. The whole list arrives in one response (a few hundred profiles),
 * so search, filters and sorting run in the browser and respond on every keystroke.
 * Filters live in the URL so a filtered view can be shared.
 */

const SORTS = [
  { id: 'relevance', label: 'Relevance to you' },
  { id: 'recent', label: 'Most recently reported' },
  { id: 'techniques', label: 'Most techniques' },
  { id: 'name', label: 'Name' },
];

function Select({ label, value, onChange, options, all = 'All' }) {
  return (
    <label className="ac-filter">
      <span className="sr-only">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        <option value="">{label}: {all}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}{o.count != null ? ` (${o.count})` : ''}</option>)}
      </select>
    </label>
  );
}

export default function ActorsDirectory() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const res = useResource(() => apiGet('/actors'), []);
  const d = res.data;
  const q = params.get('q') || '';
  const scope = params.get('scope') || 'relevant';
  const type = params.get('type') || '';
  const origin = params.get('origin') || '';
  const motive = params.get('motive') || '';
  const sector = params.get('sector') || '';
  const sort = params.get('sort') || 'relevance';

  const set = useCallback((patch) => {
    setParams((p) => {
      const next = new URLSearchParams(p);
      Object.entries(patch).forEach(([k, v]) => (!v ? next.delete(k) : next.set(k, v)));
      return next;
    }, { replace: true });
  }, [setParams]);

  const actors = useMemo(() => d?.actors || [], [d]);
  const facets = useMemo(() => {
    const count = (fn) => {
      const m = new Map();
      actors.forEach((a) => fn(a).forEach(([k, l]) => { const e = m.get(k) || { value: k, label: l, count: 0 }; e.count += 1; m.set(k, e); }));
      return [...m.values()].sort((x, y) => y.count - x.count || x.label.localeCompare(y.label));
    };
    return {
      types: count((a) => [[a.type, a.type_label]]),
      origins: count((a) => (a.origin ? [[a.origin.iso2, a.origin.name]] : [])),
      motives: count((a) => a.motivations.map((m) => [m.id, m.label])),
      sectors: count((a) => a.targets.sectors.map((s) => [s, s])),
    };
  }, [actors]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = actors.filter((a) => {
      if (scope === 'relevant' && !a.relevance.score) return false;
      if (type && a.type !== type) return false;
      if (origin && a.origin?.iso2 !== origin) return false;
      if (motive && !a.motivations.some((m) => m.id === motive)) return false;
      if (sector && !a.targets.sectors.includes(sector)) return false;
      if (needle) {
        const hay = [a.name, ...a.aliases, ...a.malware, a.mitre?.id || '', a.sponsor || ''].join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
    const by = {
      relevance: (a, b) => b.relevance.score - a.relevance.score || String(b.reporting.last_reported || '').localeCompare(String(a.reporting.last_reported || '')),
      recent: (a, b) => String(b.reporting.last_reported || '').localeCompare(String(a.reporting.last_reported || '')),
      techniques: (a, b) => b.technique_count - a.technique_count,
      name: () => 0,
    }[sort] || (() => 0);
    rows = [...rows].sort((a, b) => by(a, b) || a.name.localeCompare(b.name));
    return rows;
  }, [actors, q, scope, type, origin, motive, sector, sort]);

  const anyFilter = q || type || origin || motive || sector;
  const s = d?.stats;

  return (
    <div className="pg ac" data-testid="actors-page">
      <PageHeader
        title="Threat actors"
        subtitle="Who is behind the threats: what each group targets, the malware and techniques it uses, and what our reporting has seen. Built on MITRE ATT&CK, enriched by IntelliSecure reporting and analysts."
      />

      {res.loading && !d ? <LoadingState label="threat actors" rows={8} />
        : res.error && !d ? <ErrorState title="Couldn't load threat actors" error={res.error} onRetry={res.reload} />
          : (
            <>
              <section className="is-stats" aria-label="Key numbers">
                <button type="button" className="is-stats-item ac-stat-btn" onClick={() => set({ scope: 'all' })}>
                  <span className="is-stats-l">Profiled actors</span><b className="is-stats-v">{s.total}</b><span className="is-stats-s">ATT&CK groups and analyst profiles</span>
                </button>
                <button type="button" className="is-stats-item ac-stat-btn" onClick={() => set({ scope: null })}>
                  <span className="is-stats-l">Target your organization</span><b className="is-stats-v">{s.targeting_you}</b>
                  <span className="is-stats-s">{d.org.sector ? `${d.org.sector}` : 'Your sector'}{d.org.region ? ` or the ${d.org.region}` : ''}</span>
                </button>
                <button type="button" className="is-stats-item ac-stat-btn" onClick={() => set({ scope: null, sort: 'relevance' })}>
                  <span className="is-stats-l">In your threats</span><b className="is-stats-v">{s.in_your_threats}</b><span className="is-stats-s">Attributed or mentioned</span>
                </button>
                <button type="button" className="is-stats-item ac-stat-btn" onClick={() => set({ scope: 'all', sort: 'recent' })}>
                  <span className="is-stats-l">Reported in 90 days</span><b className="is-stats-v">{s.reported_90d}</b><span className="is-stats-s">In IntelliSecure stories</span>
                </button>
              </section>

              <div className="ac-toolbar">
                <div className="is-seg" role="group" aria-label="Which actors">
                  <button type="button" aria-pressed={scope === 'relevant'} onClick={() => set({ scope: null })} data-testid="scope-relevant">Relevant to you</button>
                  <button type="button" aria-pressed={scope === 'all'} onClick={() => set({ scope: 'all' })} data-testid="scope-all">All actors</button>
                </div>
                <label className="ac-search">
                  <Search aria-hidden="true" />
                  <input value={q} onChange={(e) => set({ q: e.target.value })} placeholder="Name, alias, malware or ATT&CK ID" aria-label="Search threat actors" data-testid="actor-search" />
                  {q && <button type="button" onClick={() => set({ q: null })} aria-label="Clear search"><X aria-hidden="true" /></button>}
                </label>
                <Select label="Type" value={type} onChange={(v) => set({ type: v })} options={facets.types} />
                <Select label="Origin" value={origin} onChange={(v) => set({ origin: v })} options={facets.origins} />
                <Select label="Motivation" value={motive} onChange={(v) => set({ motive: v })} options={facets.motives} />
                <Select label="Targets" value={sector} onChange={(v) => set({ sector: v })} options={facets.sectors} all="Any sector" />
                <Select label="Sort" value={sort === 'relevance' ? '' : sort} onChange={(v) => set({ sort: v })} options={SORTS.slice(1).map((x) => ({ value: x.id, label: x.label }))} all="Relevance to you" />
              </div>

              <p className="ac-count" role="status">
                {shown.length} of {actors.length} actors{scope === 'relevant' ? ' relevant to you' : ''}
                {anyFilter && <button type="button" className="ac-clear" onClick={() => set({ q: null, type: null, origin: null, motive: null, sector: null })}>Clear filters</button>}
              </p>

              {shown.length === 0 ? (
                <div className="ac-empty">
                  <b>No actor matches</b>
                  <span>{scope === 'relevant' ? 'Nothing relevant to you matches these filters. ' : ''}Try fewer filters{scope === 'relevant' ? ' or show all actors' : ''}.</span>
                  {scope === 'relevant' && <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => set({ scope: 'all' })}>Show all actors</button>}
                </div>
              ) : (
                <div className="ac-table-wrap">
                  <table className="ac-table">
                    <thead>
                      <tr>
                        <th scope="col">Actor</th><th scope="col">Type</th><th scope="col">Origin</th><th scope="col">Targets</th>
                        <th scope="col" className="ac-num">Techniques</th><th scope="col">Last reported</th><th scope="col">Why it matters to you</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((a) => (
                        <tr key={a.id} onClick={() => navigate(`/actors/${a.slug}`)} data-testid={`actor-row-${a.slug}`}>
                          <td className="ac-td-name">
                            <Link to={`/actors/${a.slug}`} onClick={(e) => e.stopPropagation()}>{a.name}</Link>
                            {a.aliases.length > 0 && <span className="ac-aliases">{a.aliases.slice(0, 3).join(', ')}{a.aliases.length > 3 ? `, +${a.aliases.length - 3}` : ''}</span>}
                          </td>
                          <td><TypeBadge type={a.type} label={a.type_label} /></td>
                          <td className="ac-nowrap">{a.origin?.name || <span className="ac-muted">Not attributed</span>}</td>
                          <td className="ac-td-targets">{a.targets.sectors.length ? a.targets.sectors.slice(0, 3).join(', ') : <span className="ac-muted">Not stated</span>}</td>
                          <td className="ac-num">{a.technique_count}</td>
                          <td className="ac-nowrap">{a.reporting.last_reported ? ago(a.reporting.last_reported) : <span className="ac-muted">Not in our reporting</span>}</td>
                          <td className="ac-td-why">{a.relevance.reasons.length ? a.relevance.reasons.slice(0, 2).join(' · ') : <span className="ac-muted">-</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="ac-attrib">Group, technique and software data from <a href={d.attribution.url} target="_blank" rel="noopener noreferrer">{d.attribution.text}</a>{d.attack_version ? ` v${d.attack_version}` : ''}. {d.attribution.notice}</p>
            </>
          )}
    </div>
  );
}
